class WineDetailTickers extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.mobileMedia = matchMedia('(max-width: 749px)');
    this.reducedMotionMedia = matchMedia('(prefers-reduced-motion: reduce)');
    this.isIntersecting = true;
    this.measureFrame = null;
    this.pendingMeasurement = false;
    this.animationFrame = null;
    this.lastAnimationTime = null;
    this.resumeDelay = 850;
    this.nativeScrollEndDelay = 140;
    this.laneStates = Array.from(this.querySelectorAll('[data-wine-ticker-lane]'))
      .map((lane) => this.createLaneState(lane))
      .filter(Boolean);

    this.handleMediaChange = () => this.scheduleMeasurement();
    this.handleVisibilityChange = () => this.syncAnimationState();

    this.addMediaListener(this.mobileMedia, this.handleMediaChange);
    this.addMediaListener(this.reducedMotionMedia, this.handleMediaChange);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.laneStates.forEach((state) => this.addLaneListeners(state));

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasurement());
      this.resizeObserver.observe(this);
      this.laneStates.forEach((state) => {
        this.resizeObserver.observe(state.viewport);
        this.resizeObserver.observe(state.primarySet);
      });
    }

    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.isIntersecting = Boolean(entries[0]?.isIntersecting);
        this.syncAnimationState();
      });
      this.intersectionObserver.observe(this);
    }

    this.scheduleMeasurement();
    document.fonts?.ready?.then(() => {
      if (this.isConnected) this.scheduleMeasurement();
    });
  }

  disconnectedCallback() {
    if (!this.isInitialized) return;

    this.removeMediaListener(this.mobileMedia, this.handleMediaChange);
    this.removeMediaListener(this.reducedMotionMedia, this.handleMediaChange);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.laneStates.forEach((state) => this.removeLaneListeners(state));
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    if (this.measureFrame !== null) cancelAnimationFrame(this.measureFrame);
    this.pendingMeasurement = false;
    this.stopAnimation();

    this.isInitialized = false;
  }

  createLaneState(lane) {
    const viewport = lane.querySelector('[data-wine-ticker-viewport]');
    const track = lane.querySelector('[data-wine-ticker-track]');
    const primarySet = lane.querySelector(
      '[data-wine-ticker-set]:not(.wine-details__ticker-set--duplicate)'
    );
    if (!viewport || !track || !primarySet) return null;

    const centerIndex = Math.max(Array.from(track.children).indexOf(primarySet), 1);

    return {
      lane,
      viewport,
      track,
      primarySet,
      scope: lane.dataset.tickerScope || 'mobile',
      speed: Math.max(Number(lane.dataset.speed) || 10, 1),
      direction: lane.dataset.direction === 'reverse' ? -1 : 1,
      centerIndex,
      centerOffset: 0,
      loopWidth: 0,
      position: 0,
      isLooping: false,
      isRunning: false,
      userControlled: false,
      pointerActive: false,
      focusControlled: false,
      pointerId: null,
      pointerType: null,
      dragAxis: null,
      dragStartX: 0,
      dragStartY: 0,
      dragStartScroll: 0,
      nativeScrolling: false,
      nativeScrollEndTimer: null,
      resumeTimer: null,
    };
  }

  addLaneListeners(state) {
    state.handlePointerDown = (event) => this.beginPointerControl(state, event);
    state.handlePointerMove = (event) => this.movePointerControl(state, event);
    state.handlePointerEnd = (event) => this.endPointerControl(state, event);
    state.handleScroll = () => this.handleUserScroll(state);
    state.handleScrollEnd = () => this.finishNativeScroll(state);
    state.handleWheel = (event) => this.handleUserWheel(state, event);
    state.handleFocusIn = () => {
      state.focusControlled = true;
      this.beginUserControl(state);
    };
    state.handleFocusOut = () => {
      state.focusControlled = false;
      this.scheduleResume(state);
    };
    state.handleKeyDown = (event) => this.handleLaneKeyDown(state, event);

    state.viewport.addEventListener('pointerdown', state.handlePointerDown);
    state.viewport.addEventListener('pointermove', state.handlePointerMove, { passive: false });
    state.viewport.addEventListener('pointerup', state.handlePointerEnd);
    state.viewport.addEventListener('pointercancel', state.handlePointerEnd);
    state.viewport.addEventListener('lostpointercapture', state.handlePointerEnd);
    state.viewport.addEventListener('scroll', state.handleScroll, { passive: true });
    state.viewport.addEventListener('scrollend', state.handleScrollEnd);
    state.viewport.addEventListener('wheel', state.handleWheel, { passive: true });
    state.lane.addEventListener('focusin', state.handleFocusIn);
    state.lane.addEventListener('focusout', state.handleFocusOut);
    state.lane.addEventListener('keydown', state.handleKeyDown);
  }

  removeLaneListeners(state) {
    state.viewport.removeEventListener('pointerdown', state.handlePointerDown);
    state.viewport.removeEventListener('pointermove', state.handlePointerMove);
    state.viewport.removeEventListener('pointerup', state.handlePointerEnd);
    state.viewport.removeEventListener('pointercancel', state.handlePointerEnd);
    state.viewport.removeEventListener('lostpointercapture', state.handlePointerEnd);
    state.viewport.removeEventListener('scroll', state.handleScroll);
    state.viewport.removeEventListener('scrollend', state.handleScrollEnd);
    state.viewport.removeEventListener('wheel', state.handleWheel);
    state.lane.removeEventListener('focusin', state.handleFocusIn);
    state.lane.removeEventListener('focusout', state.handleFocusOut);
    state.lane.removeEventListener('keydown', state.handleKeyDown);
    if (state.resumeTimer !== null) clearTimeout(state.resumeTimer);
    if (state.nativeScrollEndTimer !== null) clearTimeout(state.nativeScrollEndTimer);
    state.track.style.transform = '';
  }

  beginPointerControl(state, event) {
    if (!state.isLooping) return;

    state.pointerActive = true;
    state.pointerId = event.pointerId;
    state.pointerType = event.pointerType || 'mouse';
    state.dragAxis = state.pointerType === 'touch'
      ? 'native'
      : state.pointerType === 'mouse'
        ? 'horizontal'
        : null;
    if (state.pointerType === 'touch') {
      this.cancelNativeScrollEnd(state);
      state.nativeScrolling = true;
    } else if (state.nativeScrolling) {
      this.cancelNativeScrollEnd(state);
      state.nativeScrolling = false;
      this.recenterPosition(state);
    }
    this.beginUserControl(state);
    state.dragStartX = event.clientX;
    state.dragStartY = event.clientY;
    state.dragStartScroll = state.viewport.scrollLeft;

    if (state.dragAxis === 'horizontal') {
      this.capturePointer(state, event.pointerId);
      event.preventDefault();
    }
  }

  movePointerControl(state, event) {
    if (!state.pointerActive || event.pointerId !== state.pointerId) return;
    if (state.pointerType === 'touch') return;

    const distanceX = event.clientX - state.dragStartX;
    const distanceY = event.clientY - state.dragStartY;

    if (state.dragAxis === null) {
      if (Math.max(Math.abs(distanceX), Math.abs(distanceY)) < 6) return;

      if (Math.abs(distanceY) > Math.abs(distanceX)) {
        state.dragAxis = 'vertical';
        return;
      }

      state.dragAxis = 'horizontal';
      this.capturePointer(state, event.pointerId);
    }

    if (state.dragAxis !== 'horizontal') return;

    state.viewport.scrollLeft = state.dragStartScroll - distanceX;
    this.recenterPosition(state);
    event.preventDefault();
  }

  endPointerControl(state, event) {
    if (!state.pointerActive) return;
    if (state.pointerId !== null && event.pointerId !== undefined && event.pointerId !== state.pointerId) return;

    const usedNativeScroll = state.pointerType === 'touch';
    if (state.dragAxis === 'horizontal') this.recenterPosition(state);

    const pointerId = state.pointerId;
    state.pointerActive = false;
    state.pointerId = null;

    if (pointerId !== null) {
      try {
        if (state.viewport.hasPointerCapture?.(pointerId)) {
          state.viewport.releasePointerCapture(pointerId);
        }
      } catch (_error) {
        // The browser may already have released implicit pointer capture.
      }
    }

    state.pointerType = null;
    state.dragAxis = null;
    if (usedNativeScroll) this.scheduleNativeScrollEnd(state);
    else if (this.pendingMeasurement) this.scheduleMeasurement();
    else this.scheduleResume(state);
  }

  capturePointer(state, pointerId) {
    try {
      state.viewport.setPointerCapture?.(pointerId);
    } catch (_error) {
      // A touch pointer can be cancelled while its direction is being resolved.
    }
  }

  handleUserScroll(state) {
    if (!state.userControlled) return;

    if (state.nativeScrolling) {
      this.scheduleNativeScrollEnd(state);
      return;
    }

    this.recenterPosition(state);
    if (!state.pointerActive && !state.focusControlled) this.scheduleResume(state);
  }

  handleUserWheel(state, event) {
    if (Math.abs(event.deltaX) < 0.5) return;

    this.beginUserControl(state);
    state.nativeScrolling = true;
    this.scheduleNativeScrollEnd(state);
  }

  handleLaneKeyDown(state, event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.beginUserControl(state);
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    state.viewport.scrollLeft += direction * 48;
    this.recenterPosition(state);
  }

  beginUserControl(state) {
    if (state.resumeTimer !== null) {
      clearTimeout(state.resumeTimer);
      state.resumeTimer = null;
    }

    if (!state.userControlled) this.transferAnimationToNative(state);
    state.userControlled = true;
    state.lane.classList.add('is-user-controlled');
  }

  cancelNativeScrollEnd(state) {
    if (state.nativeScrollEndTimer === null) return;
    clearTimeout(state.nativeScrollEndTimer);
    state.nativeScrollEndTimer = null;
  }

  scheduleNativeScrollEnd(state) {
    this.cancelNativeScrollEnd(state);
    state.nativeScrollEndTimer = setTimeout(() => {
      state.nativeScrollEndTimer = null;
      this.finishNativeScroll(state);
    }, this.nativeScrollEndDelay);
  }

  finishNativeScroll(state) {
    if (!state.nativeScrolling) return;
    if (state.pointerActive && state.pointerType === 'touch') return;

    this.cancelNativeScrollEnd(state);
    state.nativeScrolling = false;
    this.recenterPosition(state);
    if (this.pendingMeasurement) this.scheduleMeasurement();
    else this.scheduleResume(state);
  }

  scheduleResume(state) {
    if (state.pointerActive || state.focusControlled || state.nativeScrolling) return;
    if (state.resumeTimer !== null) clearTimeout(state.resumeTimer);

    state.resumeTimer = setTimeout(() => {
      state.resumeTimer = null;
      this.recenterPosition(state);
      state.userControlled = false;
      state.lane.classList.remove('is-user-controlled');
      if (state.isRunning) this.prepareAutoplay(state);
      this.startAnimation();
    }, this.resumeDelay);
  }

  wrapPosition(position, loopWidth) {
    if (!(loopWidth > 0)) return Math.max(Number(position) || 0, 0);
    return ((position % loopWidth) + loopWidth) % loopWidth;
  }

  getCenterOffset(state) {
    return state.centerOffset > 0 ? state.centerOffset : state.loopWidth;
  }

  recenterPosition(state) {
    let physicalPosition = Number(state.viewport.scrollLeft) || 0;

    if (!state.isLooping || !(state.loopWidth > 0)) {
      state.position = Math.max(physicalPosition, 0);
      return state.viewport.scrollLeft;
    }

    const centerOffset = this.getCenterOffset(state);
    const originalPosition = physicalPosition;
    while (physicalPosition < centerOffset) physicalPosition += state.loopWidth;
    while (physicalPosition >= centerOffset + state.loopWidth) physicalPosition -= state.loopWidth;

    const recenterDelta = physicalPosition - originalPosition;
    if (Math.abs(recenterDelta) > 0.01) {
      state.viewport.scrollLeft = physicalPosition;
      if (state.pointerActive && state.dragAxis === 'horizontal') {
        state.dragStartScroll += recenterDelta;
      }
    }

    state.position = this.wrapPosition(physicalPosition - centerOffset, state.loopWidth);
    return physicalPosition;
  }

  transferAnimationToNative(state) {
    if (!state.isLooping || !(state.loopWidth > 0)) {
      state.track.style.transform = '';
      return;
    }

    state.position = this.wrapPosition(state.position, state.loopWidth);
    state.viewport.scrollLeft = this.getCenterOffset(state) + state.position;
    state.track.style.transform = '';
  }

  prepareAutoplay(state) {
    if (!state.isRunning || !state.isLooping || !(state.loopWidth > 0)) return;

    this.recenterPosition(state);
    state.viewport.scrollLeft = this.getCenterOffset(state);
    this.renderAnimatedPosition(state);
  }

  renderAnimatedPosition(state) {
    state.track.style.transform = `translate3d(${-state.position}px, 0, 0)`;
  }

  addMediaListener(mediaQuery, listener) {
    if (typeof mediaQuery.addEventListener === 'function') mediaQuery.addEventListener('change', listener);
    else mediaQuery.addListener(listener);
  }

  removeMediaListener(mediaQuery, listener) {
    if (typeof mediaQuery.removeEventListener === 'function') mediaQuery.removeEventListener('change', listener);
    else mediaQuery.removeListener(listener);
  }

  scheduleMeasurement() {
    if (this.measureFrame !== null) cancelAnimationFrame(this.measureFrame);
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      this.measureLanes();
    });
  }

  measureLanes() {
    if (this.laneStates.some((state) => state.nativeScrolling || state.pointerActive)) {
      this.pendingMeasurement = true;
      return;
    }

    this.pendingMeasurement = false;
    const isMobile = this.mobileMedia.matches;
    const prefersReducedMotion = this.reducedMotionMedia.matches;

    this.stopAnimation();
    this.classList.toggle('is-mobile', isMobile);
    this.classList.toggle('is-reduced-motion', prefersReducedMotion);

    this.laneStates.forEach((state) => {
      const previousPosition = state.userControlled && state.isLooping && state.loopWidth > 0
        ? state.viewport.scrollLeft - this.getCenterOffset(state)
        : state.position;
      const previousPhase = state.loopWidth > 0
        ? this.wrapPosition(previousPosition, state.loopWidth) / state.loopWidth
        : 0;

      state.track.style.transform = '';
      state.lane.classList.remove('is-looping', 'is-running');
      state.isLooping = false;
      state.isRunning = false;
      this.cancelNativeScrollEnd(state);
      state.nativeScrolling = false;

      const participatesAtCurrentWidth = state.scope === 'all' || isMobile;

      if (!participatesAtCurrentWidth) {
        state.lane.removeAttribute('tabindex');
        state.viewport.scrollLeft = 0;
        state.position = 0;
        state.loopWidth = 0;
        state.centerOffset = 0;
        state.userControlled = false;
        state.lane.classList.remove('is-user-controlled');
        if (state.resumeTimer !== null) clearTimeout(state.resumeTimer);
        state.resumeTimer = null;
        return;
      }

      const contentWidth = Math.max(state.primarySet.scrollWidth, state.primarySet.getBoundingClientRect().width);
      const viewportWidth = state.viewport.clientWidth;
      const contentOverflows = contentWidth > viewportWidth + 1;
      const shouldRun = contentOverflows && !prefersReducedMotion;

      state.loopWidth = contentWidth;
      state.centerOffset = contentWidth * state.centerIndex;
      state.isLooping = contentOverflows;
      state.isRunning = shouldRun;
      state.lane.classList.toggle('is-looping', contentOverflows);
      state.lane.classList.toggle('is-running', shouldRun);

      if (contentOverflows) state.lane.setAttribute('tabindex', '0');
      else state.lane.removeAttribute('tabindex');

      if (!contentOverflows) {
        state.position = 0;
        state.viewport.scrollLeft = 0;
        state.userControlled = false;
        state.lane.classList.remove('is-user-controlled');
        if (state.resumeTimer !== null) clearTimeout(state.resumeTimer);
        state.resumeTimer = null;
      } else {
        state.position = this.wrapPosition(previousPhase * contentWidth, contentWidth);
        if (shouldRun && !state.userControlled) {
          state.viewport.scrollLeft = state.centerOffset;
          this.renderAnimatedPosition(state);
        } else {
          state.viewport.scrollLeft = state.centerOffset + state.position;
        }
      }

      if (state.userControlled && !state.pointerActive && !state.focusControlled) {
        this.scheduleResume(state);
      }
    });

    this.syncAnimationState();
  }

  canAnimate() {
    return (
      this.isConnected &&
      !this.reducedMotionMedia.matches &&
      this.isIntersecting &&
      !document.hidden
    );
  }

  syncAnimationState() {
    if (this.canAnimate()) this.startAnimation();
    else this.stopAnimation();
  }

  startAnimation() {
    if (this.animationFrame !== null || !this.canAnimate()) return;
    if (!this.laneStates.some((state) => state.isRunning && !state.userControlled)) return;

    this.lastAnimationTime = null;
    this.animationFrame = requestAnimationFrame((time) => this.animate(time));
  }

  stopAnimation() {
    if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
    this.lastAnimationTime = null;
  }

  animate(time) {
    this.animationFrame = null;
    if (!this.canAnimate()) {
      this.lastAnimationTime = null;
      return;
    }

    const elapsed = this.lastAnimationTime === null ? 0 : Math.max(time - this.lastAnimationTime, 0);
    this.lastAnimationTime = time;
    let hasActiveLane = false;

    this.laneStates.forEach((state) => {
      if (!state.isRunning || state.userControlled || state.loopWidth <= 0) return;

      hasActiveLane = true;
      const direction = state.direction === -1 ? -1 : 1;
      state.position = this.wrapPosition(
        state.position + (direction * state.speed * elapsed) / 1000,
        state.loopWidth
      );
      this.renderAnimatedPosition(state);
    });

    if (hasActiveLane) this.animationFrame = requestAnimationFrame((nextTime) => this.animate(nextTime));
    else this.lastAnimationTime = null;
  }
}

if (!customElements.get('wine-detail-tickers')) {
  customElements.define('wine-detail-tickers', WineDetailTickers);
}
